import React from 'react';
import LegalPage, { CONTACT_EMAIL } from '../components/LegalPage';

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="This Privacy Policy explains what information One13 collects, why, how we use and protect it, and your rights. It is written to align with the Nigeria Data Protection Regulation (NDPR) and the Nigeria Data Protection Act. By using One13, you agree to this Policy."
      otherHref="/terms"
      otherLabel="Terms of Service"
    >
      <h2>1. Who this covers</h2>
      <p>
        One13 is a bookkeeping and inventory app for traders (“you”, the “business owner” and your
        authorised staff). Because you record your own business’s transactions and your customers’
        details, this Policy covers two kinds of people:
      </p>
      <ul>
        <li>
          <strong>You and your staff</strong> — the people who hold One13 accounts.
        </li>
        <li>
          <strong>Your customers and suppliers</strong> — whose names and transaction details you
          enter into One13 to run your books.
        </li>
      </ul>
      <div className="legal-callout">
        <p>
          <strong>Your responsibility as the business:</strong> When you enter your customers’ or
          suppliers’ details into One13, you are the primary data controller for that information,
          and you are responsible for having a lawful basis to record it and for informing those
          people as your own local law requires. One13 processes it on your behalf to provide the
          bookkeeping service.
        </p>
      </div>

      <h2>2. Information we collect</h2>
      <h3>Account &amp; business information</h3>
      <ul>
        <li>Your name, email address, phone number, and password (stored securely / hashed).</li>
        <li>Your business name, address, branches, and staff accounts you create.</li>
      </ul>
      <h3>Business records you enter</h3>
      <ul>
        <li>Sales, expenses, purchase orders, stock, and payments.</li>
        <li>Names and balances of your customers (debtors) and suppliers, as you record them.</li>
      </ul>
      <h3>Technical information</h3>
      <ul>
        <li>
          Basic device and app information needed to run the service and diagnose problems (e.g. app
          version, error logs).
        </li>
      </ul>
      <p>
        We do not intentionally collect sensitive personal data (such as health, religion, or
        biometric data). Please do not enter such data into free-text fields.
      </p>

      <h2>3. How we use your information</h2>
      <ul>
        <li>
          To provide the One13 service — recording your transactions and producing your reports and
          financial statements.
        </li>
        <li>To create and secure your account and authenticate you and your staff.</li>
        <li>To provide support and respond to your requests.</li>
        <li>To maintain, improve, and secure the app.</li>
        <li>
          To communicate service information (e.g. important notices, and — where applicable —
          billing).
        </li>
      </ul>
      <p>
        We do not sell your data. We do not use your business records or your customers’ information
        for advertising.
      </p>

      <h2>4. Legal basis for processing</h2>
      <p>
        We process your information on the basis of: the performance of our agreement with you (to
        deliver the service you signed up for); your consent (where required); our legitimate
        interest in operating and securing the app; and compliance with legal obligations.
      </p>

      <h2>5. How we store and protect your information</h2>
      <ul>
        <li>
          Your data is stored using Google Firebase (Firestore and Authentication), a reputable cloud
          provider, which may store data on servers outside Nigeria. By using One13 you consent to
          this transfer, which we carry out with appropriate safeguards.
        </li>
        <li>
          We apply access controls so each business’s data is separated, and passwords are stored
          securely.
        </li>
        <li>
          No system is perfectly secure, but we take reasonable technical and organisational measures
          to protect your information.
        </li>
      </ul>

      <h2>6. Sharing your information</h2>
      <p>We share information only:</p>
      <ul>
        <li>
          With service providers who help us run the app (e.g. our cloud host), under appropriate
          confidentiality obligations.
        </li>
        <li>Where required by law or to protect rights, safety, and security.</li>
        <li>With your consent.</li>
      </ul>
      <p>We never sell your data or your customers’ data to anyone.</p>

      <h2>7. How long we keep your information</h2>
      <p>
        We keep your information for as long as your account is active and as needed to provide the
        service, and for a reasonable period afterward to meet legal, accounting, and
        dispute-resolution needs. You may request deletion (see your rights below).
      </p>

      <h2>8. Your rights (NDPR)</h2>
      <p>
        Subject to law, you may: access the personal data we hold about you; ask us to correct
        inaccurate data; ask us to delete your data; object to or restrict certain processing; and
        request a copy of your data. To exercise any right, contact us at{' '}
        <a href={'mailto:' + CONTACT_EMAIL}>{CONTACT_EMAIL}</a>. We will respond within the timeframe
        required by law.
      </p>

      <h2>9. Data breaches</h2>
      <p>
        If a data breach occurs that is likely to affect your rights, we will take prompt steps to
        address it and notify the relevant authority and affected users where the law requires.
      </p>

      <h2>10. Children</h2>
      <p>
        One13 is for businesses and is not intended for anyone under 18. We do not knowingly collect
        data from children.
      </p>

      <h2>11. Changes to this Policy</h2>
      <p>
        We may update this Policy. We will post the updated version in the app or on our website and
        update the effective date. Continued use after changes means you accept the updated Policy.
      </p>
    </LegalPage>
  );
}
